import { faCabinetFiling } from "@fortawesome/pro-regular-svg-icons/faCabinetFiling";
import { faListCheck } from "@fortawesome/pro-regular-svg-icons/faListCheck";
import { faFilePen } from "@fortawesome/pro-solid-svg-icons/faFilePen";
import { faPerson } from "@fortawesome/pro-solid-svg-icons/faPerson";
import { faPersonCircleCheck } from "@fortawesome/pro-solid-svg-icons/faPersonCircleCheck";
import { CardContent, Grid, Pagination, Stack, Typography } from "@mui/material";
import { APP_PAGE_SIZE_MEDIUM } from "app/appConstants";
import { MprcCard, MprcCardActions, MprcConfirmDialog, MprcContainer, MprcIconButton, MprcIconButtonMoreOptionsDropdown, MprcIconFa, MprcIconText, MprcJoinComponents, MprcListItemTitle, MprcRequestErrorReport, MprcRequestIndicator, MprcSpinnerIndicator, MprcText } from "components";
import { ButtonAdd } from "components/buttons";
import { MprcFilterCount, MprcFilterLine } from "components/filter";
import { MprcCardFilterBox } from "components/filter/MprcCardFilterBox";
import { MprcDropdownBuildGroupedItems, MprcDropdownItems } from "components/MprcIconButtonDropdown";
import { MprcRequestSnackbarErrorReport } from "components/MprcRequestErrorReport";
import { DevPlanCreationMode, DevPlanListViewMode, DevPlanUserListResponse, DevPlanUserListResponsePaginatedList, DevPlanUserPermissionResponse } from "generated/CoreApi_v1";
import { usePageTitle } from "hooks/usePageTitle";
import { Formaters } from "lib/Formaters";
import { enqueueSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { LmsPaths } from "routes/LmsRouter";
import { useDevPlanManagementContext } from "views/lms/devPlan/Home/components/DevPlanManagementWrapper";
import { EditDevPlanDialog } from "views/lms/devPlan/Home/Edit/EditDevPlan";
import { OperationMode } from "views/lms/devPlan/Home/Edit/EditDevPlanView/EditDevPlanView";

////////////////////////////////////////////////////////////////////////////

type MenuOptions = "edit" | "archive" | "unarchive" | "delete";

interface IProps {
    userId: string;
    viewMode: DevPlanListViewMode;
}

////////////////////////////////////////////////////////////////////////////

export const MyDevPlans: React.FC<IProps> = ({ userId, viewMode }) => {

    const ctx = useDevPlanManagementContext();

    const { t } = useTranslation("lms");
    const api = useCoreApi();
    const navigate = useNavigate();

    const [permission, setPermission] = useState<DevPlanUserPermissionResponse>({
        canCreateNewPlan: false,
        name: undefined!
    });
    const [viewData, setViewData] = useState<DevPlanUserListResponsePaginatedList | null>(null);
    const [viewError, setViewError] = useState<any>();
    const [loading, setLoading] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [dialog, setDialog] = useState<any>();

    usePageTitle(t("DevPlan.List.PageTitle"));

    //
    async function loadData() {
        setLoading(true);

        try {
            const result = await api.v1.devPlan.devPlansList({
                userId: userId,
                viewMode,
                includeArchived: ctx.includeArchived,
                pageNumber: ctx.pagination.page,
                pageSize: APP_PAGE_SIZE_MEDIUM
            });

            setViewData(result.data);
        }
        catch (e) {
            setViewData(null);
            setViewError(e);
        }
        finally {
            setLoading(false);
        }
    }

    ////////////////////////////////////////////////////////////////////////////

    const handleAdd = () => {
        setDialog(
            <EditDevPlanDialog
                devPlanId={null}
                userId={userId}
                name={viewMode == DevPlanListViewMode.Manager ? permission.name : undefined}
                onClose={(devPlanId) => {
                    if (devPlanId != null) {
                        navigate(LmsPaths.BuildDevPlanView(devPlanId, OperationMode.Edit));
                    }
                    setDialog(null);
                }}

            />
        );
    };

    const handleView = (item: DevPlanUserListResponse) => {
        const editMode = item.isDraft || viewMode == DevPlanListViewMode.Manager;
        navigate(LmsPaths.BuildDevPlanView(item.devPlanId, editMode ? OperationMode.Edit : OperationMode.View));
    };

    ///
    const getMenuItems = useCallback(
        (item: DevPlanUserListResponse): MprcDropdownItems<MenuOptions> => {

            const groups: MprcDropdownItems<MenuOptions>[] = [];
            const group0: MprcDropdownItems<MenuOptions> = [
                { text: t("Actions.Edit", { ns: "shared" }), value: "edit" },
            ];

            if (item.isArchived) {
                group0.push({ text: t("Actions.Unarchive", { ns: "shared" }), value: "unarchive" });
            }
            else if (!item.isDraft) {
                group0.push({ text: t("Actions.Archive", { ns: "shared" }), value: "archive" });
            }

            const group1: MprcDropdownItems<MenuOptions> = [
                { text: <MprcText color="error">{t("Actions.Delete", { ns: "shared" })}</MprcText>, value: "delete" },
            ];

            groups.push(group0);
            groups.push(group1);

            return MprcDropdownBuildGroupedItems<MenuOptions>(groups);
        },
        [viewData]
    );

    ///
    const handleDelete = (item: DevPlanUserListResponse) => {
        setDialog(
            <MprcConfirmDialog
                open
                severity="danger"
                message={t("DevPlan.List.MyPlans.ConfirmDeleteMessage")}
                onClose={yes => {
                    if (yes) {
                        setSaving(true);
                        api.v1.devPlan.deletePlan({ devPlanId: item.devPlanId })
                            .then(response => {
                                if (response.data === true) {
                                    enqueueSnackbar(t("Record.DeleteSuccessMessage", { ns: "shared" }), { variant: "success" });
                                    loadData();
                                }
                                else {
                                    enqueueSnackbar(t("Record.DeleteFailMessage", { ns: "shared" }), { variant: "error" });
                                }
                            })
                            .catch(e => MprcRequestSnackbarErrorReport(e))
                            .finally(() => setSaving(false));
                    }
                    setDialog(null);
                }}
            />
        );
    };

    ///
    const handleArchive = (item: DevPlanUserListResponse) => {
        setDialog(
            <MprcConfirmDialog
                open
                message={t("DevPlan.List.MyPlans.ConfirmArchiveMessage")}
                onClose={yes => {
                    if (yes) {
                        setSaving(true);
                        api.v1.devPlan.archivePlan({ devPlanId: item.devPlanId })
                            .then(response => {
                                if (response.data) {
                                    loadData();
                                    enqueueSnackbar(t("DevPlan.List.MyPlans.ArchivedMessage"), { variant: "success" });
                                } else {
                                    enqueueSnackbar(t("DevPlan.List.MyPlans.ArchivedFailMessage"), { variant: "error" });
                                }
                            })
                            .catch(e => MprcRequestSnackbarErrorReport(e))
                            .finally(() => setSaving(false));
                    }
                    setDialog(null);
                }}
            />
        );
    };

    ///
    const handleUnarchive = (item: DevPlanUserListResponse) => {
        setDialog(
            <MprcConfirmDialog
                open
                message={t("DevPlan.List.MyPlans.ConfirmUnarchiveMessage")}
                onClose={yes => {
                    if (yes) {
                        setSaving(true);
                        api.v1.devPlan.unarchivePlan({ devPlanId: item.devPlanId })
                            .then(response => {
                                if (response.data) {
                                    loadData();
                                    enqueueSnackbar(t("DevPlan.List.MyPlans.UnarchivedMessage"), { variant: "success" });
                                } else {
                                    enqueueSnackbar(t("DevPlan.List.MyPlans.UnarchivedFailMessage"), { variant: "error" });
                                }
                            })
                            .catch(e => MprcRequestSnackbarErrorReport(e))
                            .finally(() => setSaving(false));
                    }
                    setDialog(null);
                }}
            />
        );
    };

    ///
    const handleSelectOption = (option: MenuOptions, item: DevPlanUserListResponse) => {
        switch (option) {
            case "edit":
                navigate(LmsPaths.BuildDevPlanView(item.devPlanId, OperationMode.Edit));
                break;
            case "archive":
                handleArchive(item);
                break;
            case "unarchive":
                handleUnarchive(item);
                break;
            case "delete":
                handleDelete(item);
                break;
        }
    };

    ////////////////////////////////////////////////////////////////////////////
    /// EFFECT'S
    ////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        api.v1.devPlan.getUserPermissions({ userId })
            .then(response => setPermission(response.data))
            .catch(e => setViewError(e));
    }, []);

    useEffect(() => {
        loadData();
    }, [userId, ctx.pagination, ctx.includeArchived]);


    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    ///
    const buildSecondLine = (item: DevPlanUserListResponse) => {
        let c: React.ReactNode[] = [];

        if (item.isDraft) {
            c.push(<MprcText color="warning">
                <MprcIconText
                    icon={faFilePen}
                    iconSize="lg"
                    text={t("DevPlan.List.MyPlans.DraftText")}
                    typographProps={{ textTransform: "uppercase", variant: "caption" }}
                />
            </MprcText>
            );
        }
        else if (item.isArchived) {
            c.push(<MprcText color="muted">
                <MprcIconText
                    icon={faCabinetFiling}
                    iconSize="lg"
                    text={t("DevPlan.List.MyPlans.Archived")}
                    typographProps={{ textTransform: "uppercase", variant: "caption" }}
                />
            </MprcText>
            );
        }
        else if (item.creationMode == DevPlanCreationMode.Manager) {
            c.push(<MprcText color="muted">
                <MprcIconText
                    icon={faListCheck}
                    iconSize="lg"
                    text={t("DevPlan.List.MyPlans.CreatedByManager")}
                    typographProps={{ textTransform: "uppercase", variant: "caption" }}
                />
            </MprcText>
            );
        }
        else if (item.creationMode == DevPlanCreationMode.OwnUser && item.isApproved) {
            c.push(<MprcText color="success">
                <MprcIconText
                    icon={faPersonCircleCheck}
                    iconSize="lg"
                    text={t("DevPlan.List.MyPlans.ApprovedOwnPlan")}
                    typographProps={{ textTransform: "uppercase", variant: "caption" }}
                />
            </MprcText>
            );
        }
        else if (item.creationMode == DevPlanCreationMode.OwnUser) {
            c.push(<MprcText color="primary">
                <MprcIconText
                    icon={faPerson}
                    iconSize="lg"
                    text={t("DevPlan.List.MyPlans.OwnPlan")}
                    typographProps={{ textTransform: "uppercase", variant: "caption" }}
                />
            </MprcText>
            );
        }

        if (!item.isDraft) {

            if (item.pendingStart && item.availableStarting != null) {
                c.push(
                    <MprcText color="warning">
                        {t("DevPlan.List.MyPlans.AvailableAtText", { date: Formaters.dayMonth(item.availableStarting) })}
                    </MprcText>
                );
            }


            c.push(
                <Typography variant="caption" color="muted.main">
                    {t("DevPlan.List.MyPlans.CompletionPercent", { value: Formaters.localeNumber(item.completionPercent) })}
                </Typography>
            );
        }

        if (item.totalItems > 0) {
            c.push(t("DevPlan.List.MyPlans.TotalItems", { count: item.totalItems }));
        }

        if (!item.isDraft && item.totalDelayedItems > 0) {
            c.push(
                <MprcText color="error">
                    {t("DevPlan.List.MyPlans.TotalItemsDelayed", { count: item.totalDelayedItems })}
                </MprcText>
            );
        }

        return (
            <Typography variant="caption">
                <Stack direction="row" alignItems="center">
                    <MprcJoinComponents components={c} separator={<span>&nbsp;&bull;&nbsp;</span>} />
                </Stack>
            </Typography>
        );
    };


    ///
    const renderContent = () => {
        return (
            <>
                <CardContent>
                    {loading && <MprcSpinnerIndicator />}

                    {
                        !loading && viewData && (
                            <>
                                <MprcContainer>
                                    <MprcFilterCount
                                        loading={loading}
                                        isEmpty={viewData!.totalRows == 0}
                                        countText={t("DevPlan.List.MyPlans.RecordsFoundText", { count: viewData!.totalRows })}
                                    />
                                </MprcContainer>

                                <Grid container justifyContent="center" alignItems="center" spacing={2}>
                                    {viewData!.items.map((item, index) => {
                                        return (
                                            <Grid key={index} alignItems="center" justifyContent="center" size={{ xs: 12 }}>
                                                <Grid container justifyContent="flex-start" alignItems="flex-start" spacing={1} wrap="nowrap">
                                                    <Grid size="grow">
                                                        <Grid container direction="row">
                                                            <Grid size={{ xs: 12 }}>
                                                                <Grid container alignItems="center" wrap="nowrap">
                                                                    <Grid >
                                                                        <MprcListItemTitle onClick={() => handleView(item)}>
                                                                            {item.name}
                                                                        </MprcListItemTitle>
                                                                    </Grid>
                                                                    <Grid>
                                                                        {item.canManage &&
                                                                            <MprcIconButtonMoreOptionsDropdown
                                                                                color="muted"
                                                                                onChange={(selection) => handleSelectOption(selection, item)}
                                                                                items={getMenuItems(item)}
                                                                            />
                                                                        }
                                                                    </Grid>
                                                                </Grid>
                                                            </Grid>
                                                            <Grid size={{ xs: 12 }} sx={(theme) => ({ mt: item.canManage ? theme.spacing(-.5) : 1 })}>
                                                                {buildSecondLine(item)}
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        );
                                    })
                                    }
                                </Grid>
                            </>
                        )}
                </CardContent>
                {!loading && viewData && viewData.totalPages > 1 && (
                    <MprcCardActions>
                        <Pagination
                            shape="rounded"
                            color="primary"
                            page={viewData.pageNumber}
                            count={viewData.totalPages}
                            onChange={(e, pageNumber) => {
                                if (pageNumber != ctx.pagination.page) {
                                    ctx.setPagination({ page: pageNumber });
                                }
                            }}
                        />
                    </MprcCardActions>
                )}
            </>
        );
    };

    ///
    return (
        <>
            <MprcRequestIndicator visible={saving} type={"post"} />
            {dialog}
            {viewError
                ? <MprcRequestErrorReport error={viewError} />
                : <MprcContainer>
                    <MprcCard>
                        <MprcCardFilterBox>
                            <MprcFilterLine>
                                <Grid container alignItems="center" width="100%" spacing={1}>
                                    <Grid size="grow">
                                        <Stack alignItems="flex-start">
                                            {permission.canCreateNewPlan ?
                                                <ButtonAdd
                                                    size="large"
                                                    color="inherit"
                                                    onClick={handleAdd}
                                                    text={viewMode == DevPlanListViewMode.Manager ? t("DevPlan.List.NewDevPlanButtonText") : t("DevPlan.List.NewOwnDevPlanButtonText")}
                                                />
                                                :
                                                <Typography variant="h3">{t("DevPlan.List.MyPlansTabTitle")}</Typography>
                                            }
                                            {viewMode == DevPlanListViewMode.Manager && <Typography variant="caption" ml={5.5} mb={0.5}>{permission.name}</Typography>}
                                        </Stack>
                                    </Grid>
                                    <Grid>
                                        <MprcIconButton
                                            color={!ctx.includeArchived ? "inherit" : "warning"}
                                            onClick={() => ctx.setIncludeArchived(!ctx.includeArchived)}
                                            title={t("DevPlan.List.MyPlans.IncludeArchiveTooltip")}
                                        >
                                            <MprcIconFa icon={faCabinetFiling} />
                                        </MprcIconButton>
                                    </Grid>
                                </Grid>
                            </MprcFilterLine>
                        </MprcCardFilterBox>
                        {renderContent()}
                    </MprcCard>
                </MprcContainer>
            }
        </>
    );
};
